import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil, X } from "lucide-react";
import { api } from "@/api";

const inputCls =
    "w-full px-3 py-2.5 rounded-lg bg-white border border-[#E6DBCF] text-sm text-[#241D19] focus:outline-none focus:ring-2 focus:ring-[#C85A17]/40";

export const BlogManager = () => {
    const [posts, setPosts] = useState([]);
    const [form, setForm] = useState(null);

    const load = () =>
        api.get("/posts").then(({ data }) => setPosts(data)).catch(() => toast.error("Could not load posts"));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.title.trim() || !form.body.trim()) {
            toast.error("Title and content are required");
            return;
        }
        try {
            if (form.id) {
                await api.put(`/admin/posts/${form.id}`, { title: form.title, body: form.body });
            } else {
                await api.post("/admin/posts", { title: form.title, body: form.body });
            }
            toast.success("Post saved");
            setForm(null);
            load();
        } catch {
            toast.error("Could not save post");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this post permanently?")) return;
        try {
            await api.delete(`/admin/posts/${id}`);
            setPosts((ps) => ps.filter((p) => p.id !== id));
            toast.success("Post deleted");
        } catch {
            toast.error("Could not delete post");
        }
    };

    const uploadImage = async (id, file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/admin/posts/${id}/image`, fd);
            toast.success("Image uploaded");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        }
    };

    return (
        <div data-testid="admin-blog-manager">
            <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-[#6E6259]">{posts.length} post{posts.length === 1 ? "" : "s"} published</p>
                <button
                    data-testid="admin-blog-new-button"
                    onClick={() => setForm({ title: "", body: "" })}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#C85A17] text-white text-xs font-semibold hover:bg-[#9C370B] transition-colors"
                >
                    <Plus size={14} /> New Post
                </button>
            </div>

            {form && (
                <div data-testid="admin-blog-editor" className="bg-white border border-[#E5C158] rounded-2xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">
                            {form.id ? "Edit Post" : "New Post"}
                        </h3>
                        <button onClick={() => setForm(null)} className="p-2 rounded-full hover:bg-[#F4EDE2] text-[#6E6259]" aria-label="Close editor">
                            <X size={16} />
                        </button>
                    </div>
                    <input
                        className={inputCls}
                        placeholder="Post title (e.g. How to choose the right Shradh tithi)"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        data-testid="admin-blog-title-input"
                    />
                    <textarea
                        className={`${inputCls} mt-3 min-h-[180px]`}
                        placeholder="Write the full article here..."
                        value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        data-testid="admin-blog-body-input"
                    />
                    <div className="mt-3 flex items-center gap-3">
                        {form.id ? (
                            <label className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E6DBCF] text-xs font-medium text-[#241D19] cursor-pointer hover:bg-[#F4EDE2]">
                                <Upload size={14} /> Upload Cover Image
                                <input type="file" accept="image/*" className="hidden" data-testid="admin-blog-image-input" onChange={(e) => uploadImage(form.id, e.target.files[0])} />
                            </label>
                        ) : (
                            <p className="text-xs text-[#6E6259]">Save the post first, then add a cover image.</p>
                        )}
                        <button
                            onClick={save}
                            data-testid="admin-blog-save-button"
                            className="px-6 py-2.5 rounded-full bg-[#241D19] text-white text-xs font-semibold hover:bg-[#C85A17] transition-colors"
                        >
                            Save Post
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {posts.length === 0 && !form && (
                    <p className="text-sm text-[#6E6259] bg-white border border-[#E6DBCF] rounded-2xl p-8 text-center">
                        No posts yet. Write your first article — it will appear on the website automatically.
                    </p>
                )}
                {posts.map((p) => (
                    <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-white border border-[#E6DBCF] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                        {p.image && <img src={p.image} alt="" className="w-20 h-14 rounded-lg object-cover border border-[#E6DBCF] shrink-0" />}
                        <div className="flex-1">
                            <p className="font-semibold text-[#241D19]">{p.title}</p>
                            <p className="text-xs text-[#6E6259] mt-1 line-clamp-1">{p.body}</p>
                            <p className="text-[10px] text-[#B0A494] mt-1">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <label className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#241D19] cursor-pointer hover:bg-[#F4EDE2]" title="Upload image">
                                <Upload size={15} />
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(p.id, e.target.files[0])} />
                            </label>
                            <button
                                data-testid={`admin-post-edit-${p.id}`}
                                onClick={() => setForm({ id: p.id, title: p.title, body: p.body })}
                                className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#241D19] hover:bg-[#F4EDE2]"
                                aria-label="Edit post"
                            >
                                <Pencil size={15} />
                            </button>
                            <button
                                data-testid={`admin-post-delete-${p.id}`}
                                onClick={() => remove(p.id)}
                                className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#9C370B] hover:bg-red-50"
                                aria-label="Delete post"
                            >
                                <Trash2 size={15} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
